import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router'
import { Grid, Loader } from 'semantic-ui-react'
import { DateTime } from 'luxon'

import { fetchFilteredUserTransactions } from '@/features/transactions/transactions.slice'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { GridRowColumn, Text } from '@/componentsV1/BaseComponents'
import { selectFilteredGroupedUserTransactions } from '@/features/transactions/transactions.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { userUpdateBookkeepingStepContext } from '../../end-of-year-review/actions'
import { Colors } from '../../../../styles/theme'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import { TransactionCategoryRow } from '@/componentsV1/Transactions/TransactionCategoryList'
import { useFinalReviewStepContext, useYearFromParams } from '../hooks'
import { EndOfYearAdminReviewStep } from '../../end-of-year-review/types'
import FinalReviewWrapper from '../final-review-wrapper'
import BookkeeperNote from '../bookkeeper-note'
import './styles.scss'

interface TransactionData {
  [key: string]: Transaction[]
}

export interface ContentProps {
  year?: string
  adminNote?: string
  groupedIncome: TransactionData
  totalIncome: number
  onTransactionUpdate: (id: number) => Promise<void>
  navigate: NavigateFunction
}

export const Content = ({
  year,
  adminNote,
  groupedIncome,
  totalIncome,
  onTransactionUpdate,
  navigate,
}: ContentProps) => {
  return (
    <FinalReviewWrapper
      year={year}
      step={1}
      stepBack={() =>
        navigate(
          `/taxes/annual/review-and-close-books/${year}/final-review-intro`
        )
      }
      stepForward={() =>
        navigate(
          `/taxes/annual/review-and-close-books/${year}/final-review-expenses`
        )
      }
      saveAndExit={() => navigate('/taxes/annual')}
    >
      <Grid className="income-grid">
        <GridRowColumn centerContent className="header-container">
          <StepHeaders
            imageSrc="https://heard-images.s3.amazonaws.com/assets/examine_money.svg"
            imageAlt="examine_money"
            imageWidth={180}
            imageHeight={180}
            title={`Review your ${year} income`}
            description=""
            customColor="green"
          >
            <Grid>
              <GridRowColumn className="align-left">
                <Text>
                  Please conduct a final review of your income transactions. If
                  anything looks off, add a note. Our bookkeeping team will take
                  a look and make any necessary revisions.
                </Text>
                <BookkeeperNote note={adminNote} />
              </GridRowColumn>
            </Grid>
          </StepHeaders>
        </GridRowColumn>
        <GridRowColumn
          spacer={2}
          width={12}
          columnStyle={{ backgroundColor: Colors.stone, borderRadius: 8 }}
        >
          <Grid>
            <Grid.Row className="total-transaction-grid" verticalAlign="middle">
              <Grid.Column width={8}>
                <Text as="h3">Total Income</Text>
              </Grid.Column>
              <Grid.Column width={8}>
                <Text textAlign="right" as="h3">
                  {formatCurrencyFromCents(totalIncome)}
                </Text>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </GridRowColumn>
        {Object.values(groupedIncome).map((transactions) => (
          <TransactionCategoryRow
            key={transactions[0]?.transactionCategoryId || 'null'}
            transactions={transactions}
            defaultTitle="Uncategorized Income"
            onTransactionUpdate={onTransactionUpdate}
          />
        ))}
      </Grid>
    </FinalReviewWrapper>
  )
}

const { finalReview } = EndOfYearAdminReviewStep

const FinalReviewIncome = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const year = useYearFromParams()
  const [loading, setLoading] = useState(true)
  const { finalReviewContext, setFinalReviewContext } =
    useFinalReviewStepContext()

  const startDate = useMemo(
    () =>
      DateTime.fromObject({ year: Number(year) }, { zone: 'utc' }).startOf(
        'year'
      ),
    [year]
  )
  const endDate = useMemo(
    () =>
      DateTime.fromObject({ year: Number(year) }, { zone: 'utc' }).endOf(
        'year'
      ),
    [year]
  )

  const groupedTransactions = useReselector(
    selectFilteredGroupedUserTransactions,
    { startDate, endDate }
  )
  useEffect(() => {
    const fetch = async () => {
      await Promise.all([
        dispatch(fetchFilteredUserTransactions({ startDate, endDate })),
        dispatch(fetchTransactionCategoriesIfNeeded()),
      ])

      setLoading(false)
    }

    if (startDate && endDate) {
      fetch()
    }
  }, [dispatch, startDate, endDate])

  const onTransactionUpdate = useCallback(
    async (transactionId: number) => {
      if (!finalReviewContext?.updatedTransactionIds?.includes(transactionId)) {
        const newContext = {
          ...finalReviewContext,
          updatedTransactionIds: [
            ...(finalReviewContext?.updatedTransactionIds || []),
            transactionId,
          ],
        }
        const updatedStep = await userUpdateBookkeepingStepContext(
          finalReview,
          finalReview,
          newContext
        )(dispatch)

        if (updatedStep) {
          setFinalReviewContext(updatedStep.context)
        }
      }
    },
    [dispatch, finalReviewContext, setFinalReviewContext]
  )

  return loading ? (
    <Loader loading />
  ) : (
    <Content
      year={year}
      groupedIncome={groupedTransactions.groupedIncome}
      totalIncome={groupedTransactions.totalIncome}
      adminNote={finalReviewContext?.adminNote}
      onTransactionUpdate={onTransactionUpdate}
      navigate={navigate}
    />
  )
}

export default FinalReviewIncome
